import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";
import api from "api";
import { useSelector, useDispatch } from "react-redux";
import QuickFilter from "./components/QuickFilter";
import TopPanelComponent from "./components/TopPanelComponent";
import HealthPlanSelect from "components/common/HealthPlanSelect";
import StyledDataGrid from "styles/styledDataGrid";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import DateSelector from "./components/DateSelector";

// Default columns when none selected
const DEFAULT_COLUMNS = [
  "ChangeControl",
  "RuleTemplate",
  "RuleEffectiveDate",
  "RuleEndDate",
  "ProcedureCode",
  "DecisionCode",
  "PolicyTag",
];

const EXCLUSION_RFGS = [
  "ExclusionFlwGrp",
  "WholesaleExclusionsSubFlowGroup",
  "PayAndEducateSubFlowGroup",
];

// Helpers
const hasValue = (row, col) => {
  const v = row[col];
  return v !== undefined && v !== null && `${v}`.trim() !== "";
};

const colsWithData = (rows, cols) =>
  cols.filter((c) => rows.some((r) => hasValue(r, c)));

function Exclusions() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Health‑plan filter
  const planKey = useSelector((s) => s.healthPlan.healthPlan?.healthPlanKey);

  // Datagrid Content
  const [clobs, setClobs] = useState([]);
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editRowData, setEditRowData] = useState({});

  // Filter State
  const [pks, setPks] = useState([]);
  const [policyKey, setPolicyKey] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [rfg, setRfg] = useState("");

  // Date Selector State
  const [dateSelectorOpen, setDateSelectorOpen] = useState(false);
  const [pendingSaveId, setPendingSaveId] = useState(null);

  // ─────────────────────────────────────────────────────────────
  // Derived Data (Memos)
  // ─────────────────────────────────────────────────────────────
  // Extract column names and section mapping from metadata
  const { allCols, colSectionMap } = useMemo(() => {
    const cols = new Set();
    const sectionMap = {};

    clobs.forEach((c) => {
      Object.values(c.workspaceClob?.RuleFlowGroupInfos || {})
        .filter((i) => EXCLUSION_RFGS.includes(i.RuleFlowGroupName))
        .filter((i) => !rfg || i.RuleFlowGroupName === rfg)
        .forEach((i) => {
          i.RuleColumns.forEach((col) => {
            cols.add(col.ColumnName);
            sectionMap[col.ColumnName] = col.JsonSectionText;
          });
        });
    });

    return {
      allCols: [...cols].sort((a, b) => a.localeCompare(b)),
      colSectionMap: sectionMap,
    };
  }, [clobs, rfg]);

  // Flatten rows for display, prioritizing 'NEW' versions
  const displayRows = useMemo(() => {
    const rowsById = {};
    rows.forEach((row) => {
      if (!rowsById[row.id] || row.EditRule === "NEW") {
        const flattened = {};
        Object.entries(row).forEach(([key, value]) => {
          if (value && typeof value === "object" && !Array.isArray(value)) {
            Object.assign(flattened, value);
          } else {
            flattened[key] = value;
          }
        });
        rowsById[row.id] = flattened;
      }
    });
    return Object.values(rowsById);
  }, [rows]);

  // Extract rule flow groups list
  const rfgs = useMemo(
    () =>
      [
        ...new Set(displayRows.map((r) => r.RuleFlowGroup).filter(Boolean)),
      ].filter((g) => EXCLUSION_RFGS.includes(g)),
    [displayRows]
  );

  // ─────────────────────────────────────────────────────────────
  // API Calls
  // ─────────────────────────────────────────────────────────────
  // Fetch policy keys with exclusion rules from backend api
  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.policyEnforcementGrid.getExclusionPolicyKeys({
        params: { healthPlanKey: planKey },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        const policyKeys = [...res.data, null];
        setPks(policyKeys);
      } else {
        setPks([]);
        console.error("Unexpected policy keys:", res);
      }
    } catch (e) {
      dispatch(
        setDialog({
          message: formErrorStrfromObj(e),
          title: "Error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [planKey, dispatch]);

  // Fetch clobs data from backend api
  // Rule set key data should be contained
  const fetchClobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        healthPlanKey: planKey,
        activeStatus: activeOnly,
        policyKey: policyKey,
      };
      const res = await api.policyEnforcementGrid.getExclusionRules({ params });
      if (res.status === 200 && Array.isArray(res.data)) {
        // Parse workspaceClob for each item
        const parsedData = res.data.map((item) => ({
          ...item,
          workspaceClob: (() => {
            try {
              return item.workspaceClob ? JSON.parse(item.workspaceClob) : {};
            } catch (e) {
              console.error("Failed to parse workspaceClob for item:", item, e);
              return {};
            }
          })(),
        }));
        console.log(parsedData);
        setClobs(parsedData);
      } else {
        setClobs([]);
        setErrorMsg("Unexpected response while loading rules.");
        console.error("Unexpected parameters:", res);
      }
    } catch (e) {
      setErrorMsg("No exclusion rules available.");
      // dispatch(
      //   setDialog({
      //     message: formErrorStrfromObj(e),
      //     title: "Error",
      //   })
      // );
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [planKey, policyKey, activeOnly, dispatch]);

  const saveChanges = useCallback(async () => {
    try {
      setLoading(true);
      const modifiedRows = rows.filter(
        (r) => r.EditRule === "NEW" || r.EditRule === "EDIT"
      );
      if (modifiedRows.length === 0) {
        setErrorMsg("No changes to save");
        return;
      }

      const matchingClob = clobs.find((c) => c.policyKey === policyKey);
      const ruleSetKey = matchingClob?.workspaceClob?.RuleSetKey;
      const ruleFlowGroupInfos =
        matchingClob?.workspaceClob?.RuleFlowGroupInfos;

      if (!ruleSetKey) {
        console.warn("No RuleSetKey found for policyKey:", policyKey);
        setErrorMsg("Unable to find RuleSetKey for the current policy");
        return;
      }

      if (!ruleFlowGroupInfos) {
        console.warn("No RuleFlowGroupInfos found for policyKey:", policyKey);
        setErrorMsg("Unable to find RuleFlowGroupInfos for the current policy");
        return;
      }

      // Remove the 'id' field we added before sending
      const cleanedRows = modifiedRows.map(({ id, ...rest }) => rest);

      const payload = {
        policyBusinessRules: cleanedRows,
        ruleFlowGroupInfos: JSON.stringify(ruleFlowGroupInfos),
      };

      console.log("Saving changes: ", ruleSetKey, payload);

      const response = await api.policyEnforcementGrid.saveExclusionRules(
        ruleSetKey,
        payload
      );

      if (response.status === 200 && response.data) {
        console.log(response.data);
        fetchClobs();
        setErrorMsg("Changes saved successfully!");
        setEditRowId(null);
        setEditRowData({});
      } else {
        setErrorMsg("Failed to save changes");
      }
    } catch (error) {
      console.log("Error saving changes:", error);
      dispatch(
        setDialog({
          message: formErrorStrfromObj(error),
          title: "Error Saving Changes",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [rows, clobs, planKey, policyKey, dispatch]);

  // ─────────────────────────────────────────────────────────────
  // Row Edit Handlers
  // ─────────────────────────────────────────────────────────────
  const handleEdit = useCallback(
    (id) => {
      const row = displayRows.find((r) => r.id === id);
      if (!row) {
        console.warn(`Row with id ${id} not found in rows`);
        return;
      }
      setEditRowId(id);
      setEditRowData({ ...row });
    },
    [displayRows]
  );

  const handleCancel = useCallback(() => {
    setEditRowId(null);
    setEditRowData({});
  }, []);

  const handleSaveClick = useCallback((id) => {
    setPendingSaveId(id);
    setDateSelectorOpen(true);
  }, []);

  // Actually performs the save after user confirms dates in modal
  const handleConfirmSave = useCallback(
    ({ endDate, effectiveDate }) => {
      const id = pendingSaveId;
      if (!id) return;

      const hasNewVersion = rows.some(
        (r) => r.id === id && r.EditRule === "NEW"
      );

      const originalRow = rows.find((r) => r.id === id);
      if (!originalRow) return;

      // Convert flattened editRowData back to nested structure using colSectionMap
      const unflattenedData = {};
      Object.entries(editRowData).forEach(([field, value]) => {
        const nestedKey = colSectionMap[field];

        if (nestedKey) {
          if (!unflattenedData[nestedKey]) {
            unflattenedData[nestedKey] = { ...(originalRow[nestedKey] || {}) };
          }
          unflattenedData[nestedKey][field] = value;
        } else {
          unflattenedData[field] = value;
        }
      });

      // Helper to normalize column names for comparison
      const normalizeColumnName = (name) =>
        name.toLowerCase().replace(/[^a-z0-9]/g, "");

      // Find date columns from available columns
      const exclusionEndDateCol = Object.keys(colSectionMap).find(
        (col) => normalizeColumnName(col) === "exclusionenddate"
      );
      const exclusionEffectiveDateCol = Object.keys(colSectionMap).find(
        (col) => normalizeColumnName(col) === "exclusioneffectivedate"
      );

      if (hasNewVersion) {
        // Update existing NEW version with the effective date
        setRows((prev) =>
          prev.map((r) => {
            if (r.id === id && r.EditRule === "NEW") {
              let updatedRow = { ...r, ...unflattenedData };

              // Set effective date on the NEW row
              if (exclusionEffectiveDateCol) {
                const nestedKey = colSectionMap[exclusionEffectiveDateCol];
                if (nestedKey) {
                  updatedRow = {
                    ...updatedRow,
                    [nestedKey]: {
                      ...(updatedRow[nestedKey] || {}),
                      [exclusionEffectiveDateCol]: effectiveDate,
                    },
                  };
                } else {
                  updatedRow[exclusionEffectiveDateCol] = effectiveDate;
                }
              }

              return updatedRow;
            }
            return r;
          })
        );
      } else {
        // Create EDIT and NEW rows with provided dates
        let editedRow = { ...originalRow, EditRule: "EDIT" };

        // Set end date on the EDIT row
        if (exclusionEndDateCol) {
          const nestedKey = colSectionMap[exclusionEndDateCol];
          if (nestedKey) {
            editedRow = {
              ...editedRow,
              [nestedKey]: {
                ...(editedRow[nestedKey] || {}),
                [exclusionEndDateCol]: endDate,
              },
            };
          } else {
            editedRow[exclusionEndDateCol] = endDate;
          }
        }

        // Prepare the NEW row with effective date
        let newRow = {
          ...originalRow,
          ...unflattenedData,
          EditRule: "NEW",
          RuleHeader: {
            ...originalRow.RuleHeader,
            ...unflattenedData.RuleHeader,
            PolicyBusinessRuleName: null,
          },
        };

        // Set effective date on the NEW row
        if (exclusionEffectiveDateCol) {
          const nestedKey = colSectionMap[exclusionEffectiveDateCol];
          if (nestedKey) {
            newRow = {
              ...newRow,
              [nestedKey]: {
                ...(newRow[nestedKey] || {}),
                [exclusionEffectiveDateCol]: effectiveDate,
              },
            };
          } else {
            newRow[exclusionEffectiveDateCol] = effectiveDate;
          }
        }

        setRows((prev) =>
          prev.flatMap((r) => (r.id === id ? [editedRow, newRow] : [r]))
        );
      }

      // Close modal and reset state
      setDateSelectorOpen(false);
      setPendingSaveId(null);
      setEditRowId(null);
      setEditRowData({});
    },
    [pendingSaveId, editRowData, rows, colSectionMap]
  );

  const handleSelectorClose = useCallback(() => {
    setDateSelectorOpen(false);
    setPendingSaveId(null);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────
  // Fetch policies when health plan key chanegs
  useEffect(() => {
    if (planKey) {
      setPolicyKey("");
      setActiveOnly(true);
      setRfg("");
      setCols([]);
      setClobs([]);
      setRows([]);

      fetchPolicies();
    }
  }, [planKey]);

  // Fetch clobs when selected filter changes
  useEffect(() => {
    if (planKey && policyKey !== "") {
      fetchClobs();
    }
  }, [policyKey, activeOnly]);

  // Compute rows from clobs
  useEffect(() => {
    const computedRows = clobs.flatMap((c) =>
      (c.workspaceClob?.PolicyBusinessRulesList || []).map((r) => ({
        ...r,
        id: `${c.policyKey}-${r.RuleKey}`,
      }))
    );
    setRows(computedRows);
  }, [clobs]);

  // Initialize default RFG when data loads
  useEffect(() => {
    if (!rfg && rfgs.length > 0) {
      setRfg(rfgs[0]);
    }
  }, [rfgs]);

  // Initialize default columns when data loads
  useEffect(() => {
    if (displayRows.length > 0 && cols.length === 0) {
      const validCols = colsWithData(displayRows, DEFAULT_COLUMNS).filter((c) =>
        allCols.includes(c)
      );

      if (validCols.length > 0) {
        setCols(validCols);
      }
    }
  }, [displayRows, allCols]);

  // Update selected columns based on available column options
  useEffect(() => {
    setCols((prev) => prev.filter((c) => allCols.includes(c)));
  }, [allCols]);

  // ─────────────────────────────────────────────────────────────
  // DataGrid Column Definitions
  // ─────────────────────────────────────────────────────────────
  // DataGrid columns
  const gridCols = useMemo(
    () =>
      cols.map((field) => ({
        field,
        headerName: field,
        flex: 1,
        sortComparator: (a, b) => {
          if (a == null && b == null) return 0;
          if (a == null) return -1;
          if (b == null) return 1;
          return String(a).localeCompare(String(b), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        },
        renderCell: (params) =>
          editRowId === params.row.id ? (
            <TextField
              variant="standard"
              fullWidth
              value={editRowData[field] || ""}
              onChange={(e) =>
                setEditRowData({
                  ...editRowData,
                  [field]: e.target.value,
                })
              }
            />
          ) : (
            params.value
          ),
      })),
    [cols, editRowId, editRowData]
  );

  // Add action column for edit & save
  const actionCol = useMemo(() => {
    return {
      field: "actions",
      flex: 1,
      sortable: false,
      headerName: "Actions",
      filterable: false,
      renderCell: (params) => {
        const isInEditMode = editRowId === params.row.id;
        return isInEditMode ? (
          <>
            <IconButton
              size="small"
              onClick={() => handleSaveClick(params.row.id)}
            >
              <SaveIcon />
            </IconButton>
            <IconButton size="small" onClick={handleCancel}>
              <CancelIcon />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton size="small" onClick={() => handleEdit(params.row.id)}>
              <EditIcon />
            </IconButton>
          </>
        );
      },
    };
  }, [editRowId, handleEdit, handleCancel, handleSaveClick]);

  const gridColsWithAction = useMemo(
    () => [...gridCols, actionCol],
    [gridCols, actionCol]
  );

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TopPanelComponent pageType="Exclusions">
        <Box
          sx={{
            width: 0.8,
          }}
          display="flex"
          px={(theme) => theme.spacing(1)}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              width: 225,
              alignSelf: "center",
              fontSize: 20,
              fontWeight: "bold",
            }}
          >
            Health Plan
          </Typography>
          <HealthPlanSelect />
          <Button
            variant="contained"
            sx={{
              width: 0.2,
              ml: 2,
              mt: 0.8,
              alignSelf: "center",
            }}
            onClick={saveChanges}
          >
            Save Changes
          </Button>
        </Box>
        <Box
          sx={{
            paddingY: 1,
          }}
        >
          <QuickFilter
            selectedRuleFlowGroup={rfg}
            onRuleFlowGroupChange={setRfg}
            policyKeys={pks}
            selectedPolicyKey={policyKey}
            onPolicyKeyChange={setPolicyKey}
            columnOptions={allCols}
            selectedColumns={cols}
            onColumnChange={(e) => setCols(e.target.value)}
            activeOnly={activeOnly}
            onActiveOnlyChange={setActiveOnly}
          />
        </Box>
      </TopPanelComponent>

      <Tabs
        value={rfg || rfgs[0] || false}
        onChange={(_e, val) => setRfg(val)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          "& .MuiTab-root": { textTransform: "none", minHeight: 30, px: 2 },
          mb: 0.5,
        }}
      >
        {rfgs.map((g) => (
          <Tab key={g} value={g} label={g} />
        ))}
      </Tabs>

      <Box flex={1} position="relative">
        <Backdrop open={loading} sx={{ zIndex: 99, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <StyledDataGrid
          rows={displayRows.filter((r) => r.RuleFlowGroup === rfg)}
          columns={gridColsWithAction}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          disableSelectionOnClick
          sx={{ height: "100%", width: "100%" }}
        />
      </Box>

      <DateSelector
        open={dateSelectorOpen}
        onClose={handleSelectorClose}
        onConfirm={handleConfirmSave}
        isNewVersion={
          pendingSaveId
            ? rows.some((r) => r.id === pendingSaveId && r.EditRule === "NEW")
            : false
        }
        rowData={pendingSaveId ? editRowData : null}
      />

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        message={errorMsg || ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

export default Exclusions;
